import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { apiErrorLog } from '../api/client';

function useDebugEnabled() {
  const [enabled, setEnabled] = useState(() =>
    new URLSearchParams(window.location.search).has('debug')
  );
  useEffect(() => {
    const onNav = () =>
      setEnabled(new URLSearchParams(window.location.search).has('debug'));
    window.addEventListener('popstate', onNav);
    return () => window.removeEventListener('popstate', onNav);
  }, []);
  return enabled;
}

export function DebugPanel() {
  const enabled = useDebugEnabled();
  const [open, setOpen] = useState(true);
  const [tick, setTick] = useState(0);
  const user = useAuthStore(s => s.user);
  const token = useAuthStore(s => s.accessToken);
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setTick(t => t + 1), 2000);
    return () => clearInterval(id);
  }, [enabled]);

  if (!enabled) return null;

  const cache = qc.getQueryCache().getAll();
  const total = cache.length;
  const loading = cache.filter(q => q.state.status === 'pending').length;
  const errors = cache.filter(q => q.state.status === 'error').length;
  const recentErrors = [...apiErrorLog].reverse().slice(0, 8);

  const s: Record<string, React.CSSProperties> = {
    panel: {
      position: 'fixed', bottom: 12, left: 12, zIndex: 9999,
      background: 'rgba(15,17,23,0.96)', border: '1px solid #374151',
      borderRadius: 8, fontFamily: 'monospace', fontSize: 11,
      color: '#e2e8f0', minWidth: 280, maxWidth: 360,
      boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
    },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '6px 10px', background: '#2a6b75', borderRadius: '7px 7px 0 0',
      cursor: 'pointer', userSelect: 'none',
    },
    body: { padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 },
    row: { display: 'flex', justifyContent: 'space-between', gap: 8 },
    label: { color: '#94a3b8' },
    val: { color: '#f1f5f9', fontWeight: 600 },
    err: { color: '#f87171', fontWeight: 600 },
    ok: { color: '#4ade80', fontWeight: 600 },
    section: { borderTop: '1px solid #1f2937', paddingTop: 6, marginTop: 2 },
    errLine: {
      fontSize: 10, color: '#fca5a5', whiteSpace: 'nowrap',
      overflow: 'hidden', textOverflow: 'ellipsis',
    },
  };

  return (
    <div style={s.panel}>
      <div style={s.header} onClick={() => setOpen(o => !o)}>
        <span>🐛 DebugPanel</span>
        <span style={{ fontSize: 10, color: '#a5f3fc' }}>{open ? '▲ chiudi' : '▼ apri'}</span>
      </div>
      {open && (
        <div style={s.body}>
          {/* Auth */}
          <div style={s.row}>
            <span style={s.label}>Utente</span>
            <span style={s.val}>{user?.email ?? '—'}</span>
          </div>
          <div style={s.row}>
            <span style={s.label}>Ruolo</span>
            <span style={s.val}>{user?.role ?? '—'}</span>
          </div>
          <div style={s.row}>
            <span style={s.label}>Token</span>
            <span style={token ? s.ok : s.err}>{token ? '✓ presente' : '✗ assente'}</span>
          </div>

          {/* React Query */}
          <div style={{ ...s.section, ...s.row }}>
            <span style={s.label}>Query cache</span>
            <span style={s.val}>{total} query</span>
          </div>
          <div style={s.row}>
            <span style={s.label}>In carico</span>
            <span style={loading > 0 ? { color: '#fbbf24', fontWeight: 600 } : s.val}>{loading}</span>
          </div>
          <div style={s.row}>
            <span style={s.label}>Errori cache</span>
            <span style={errors > 0 ? s.err : s.ok}>{errors}</span>
          </div>

          {/* API error log */}
          <div style={s.section}>
            <div style={{ ...s.label, marginBottom: 4 }}>Ultimi errori API {recentErrors.length === 0 && '(nessuno)'}</div>
            {recentErrors.map((e, i) => (
              <div key={i} style={s.errLine} title={`${e.method} ${e.path} — ${e.message}`}>
                <span style={{ color: '#f87171' }}>[{e.status}]</span>{' '}
                <span style={{ color: '#7dd3fc' }}>{e.method}</span>{' '}
                {e.path} — {e.message}
              </div>
            ))}
          </div>

          <div style={{ ...s.label, fontSize: 9, textAlign: 'right', marginTop: 2 }}>
            aggiornato ogni 2s · tick {tick}
          </div>
        </div>
      )}
    </div>
  );
}
